import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Gift, Search } from 'lucide-react';
import { EmptyState } from '@/shared/components/empty-state';
import { currency, formatDate, formatTime } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { fetchObservabilityIntegrations } from '../api/use-observability';
import type { ObservabilityExchange } from '../model/types';

/** O pedido como a Fixe leu do `POST /totaled` (gravado pelo controller da eDeploy). */
interface TotaledOrder {
  cpf?: string;
  loja?: string;
  pedido?: string;
  itens?: Array<{ sku: string; nome?: string; qtd: number; preco: number; filhas?: string[] }>;
}

/** A análise da Fixe: o que aconteceu com cada campanha que o CPF ativou. */
interface TotaledAnalysis {
  motivo?: string;
  campanhas?: Array<{ campanha: number; nome: string | null; skus: string[]; aplicada: boolean; desconto: number; motivo: string }>;
  naoAtivadas?: Array<{ campanha: number; nome: string; skus: string[] }>;
}

const STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'success', label: 'Com desconto' },
  { value: 'skipped', label: 'Sem desconto' },
] as const;

function TotaledRow({ row }: { row: ObservabilityExchange }) {
  const [open, setOpen] = useState(false);
  const order = (row.received ?? {}) as TotaledOrder;
  const analysis = (row.sent ?? {}) as TotaledAnalysis;
  const applied = row.status === 'success';
  const campaigns = analysis.campanhas ?? [];
  const missed = analysis.naoAtivadas ?? [];

  return (
    <Card className="p-4">
      <button type="button" className="w-full text-left" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={applied ? 'success' : 'secondary'}>{applied ? 'Desconto' : 'Sem desconto'}</Badge>
            <span className="font-medium">{row.storeName}</span>
            <span className="text-sm text-muted-foreground">
              loja {order.loja || '?'} · pedido {order.pedido || '?'}
            </span>
          </div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            {formatDate(row.at)} {formatTime(row.at)}
            <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
          </span>
        </div>
        <p className="mt-1 text-sm">
          {row.personName} <span className="font-mono text-xs text-muted-foreground">CPF {order.cpf || '—'}</span>
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(order.itens ?? []).length === 0 ? (
            <Badge variant="warning">nenhum item lido do pedido</Badge>
          ) : (
            (order.itens ?? []).map((item, i) => (
              <Badge key={`${item.sku}-${i}`} variant="outline" className="font-normal">
                <span className="font-mono font-medium">{item.sku}</span>
                {item.nome ? ` ${item.nome}` : ''} x{item.qtd} · {currency(item.preco)}
              </Badge>
            ))
          )}
        </div>
        {row.note ? (
          <p className={cn('mt-2 text-sm', applied ? 'text-success' : 'text-muted-foreground')}>{row.note}</p>
        ) : null}
      </button>

      {open && (campaigns.length > 0 || missed.length > 0) ? (
        <ul className="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-sm">
          {campaigns.map((c) => (
            <li key={c.campanha} className="flex flex-wrap items-baseline gap-x-2">
              <span className={c.aplicada ? 'text-success' : 'text-destructive'}>{c.aplicada ? '✓' : '✗'}</span>
              <span className="font-medium">
                #{c.campanha} {c.nome ?? ''}
              </span>
              {c.skus.length ? <span className="font-mono text-xs text-muted-foreground">SKU {c.skus.join(', ')}</span> : null}
              <span className="text-muted-foreground">— {c.aplicada ? currency(c.desconto) : c.motivo}</span>
            </li>
          ))}
          {missed.map((c) => (
            <li key={`missed-${c.campanha}`} className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-warning">!</span>
              <span className="font-medium">
                #{c.campanha} {c.nome}
              </span>
              <span className="font-mono text-xs text-muted-foreground">SKU {c.skus.join(', ')}</span>
              <span className="text-muted-foreground">— SKU no pedido, mas o CPF não ativou</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/**
 * Aba Fidelidade da Observabilidade: cada `POST /totaled` com CPF que o PDV
 * da 3S mandou (Habib's e Ragazzo), com os SKUs lidos e o motivo de ter ou
 * não ter desconto. Atualiza sozinha para acompanhar teste na loja.
 */
export function LoyaltyTotaledPanel() {
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: ['observability-loyalty', status, q],
    queryFn: () => fetchObservabilityIntegrations({ provider: 'pdv3s', direction: 'all', status, q }),
    refetchInterval: 15 * 1000,
  });
  const rows = query.data?.rows ?? [];

  return (
    <>
      <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap sm:items-center">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[10rem]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative min-w-0 sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="CPF, loja, pedido, SKU ou nome"
            className="pl-9"
            aria-label="Buscar por CPF, loja, pedido, SKU ou nome"
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Gift}
            title="Nenhum totaled registrado"
            description="Quando o PDV da 3S totalizar um pedido com CPF, ele aparece aqui com os SKUs e o motivo."
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <TotaledRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </>
  );
}
